import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    verifyInvite();
  }, [token]);

  const verifyInvite = async () => {
    try {
      const response = await axios.get(`${API}/invites/verify/${token}`);
      setInvite(response.data);
      setLoading(false);
    } catch (err) {
      const message = err.response?.data?.detail || "Invalid or expired invitation";
      setError(message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("password", password);
      
      const response = await axios.post(`${API}/invites/accept/${token}`, formData);
      
      // Save token and redirect
      localStorage.setItem("token", response.data.token);
      toast.success("Welcome to The Render Pros!");
      navigate("/");
      window.location.reload();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to create account";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-4">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-light mb-2" style={{ fontFamily: 'var(--font-sans)' }}>
              Invalid Invitation
            </h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate("/login")} className="rounded-full">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#000000] p-12 flex-col justify-between">
        <div>
          <img 
            src="/trp-logo.png" 
            alt="The Render Pros" 
            className="h-12 object-contain"
          />
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-4xl font-light text-white tracking-tight leading-tight" style={{ fontFamily: 'var(--font-sans)' }}>
              Welcome to<br />
              the team.
            </h2>
          </div>
          <p className="text-white/60 text-base max-w-md leading-relaxed">
            You've been invited to join The Render Pros Operations OS. 
            Set up your password to get started.
          </p>
        </div>

        <p className="text-white/40 text-xs">
          Built with discipline. No feature bloat.
        </p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <img 
              src="/trp-logo.png" 
              alt="The Render Pros" 
              className="h-10 object-contain mx-auto mb-2"
            />
          </div>

          <Card className="border border-border">
            <CardHeader className="text-center pb-2">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-xl" style={{ fontFamily: 'var(--font-sans)' }}>
                You're Invited!
              </CardTitle>
              <CardDescription className="mt-2">
                <strong>{invite?.invited_by_name}</strong> invited you to join as a <strong>{invite?.role}</strong>
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Email</Label>
                  <Input
                    type="email"
                    value={invite?.email || ""}
                    disabled
                    className="bg-secondary/50"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground">Full Name</Label>
                  <Input
                    type="text"
                    value={invite?.name || ""}
                    disabled
                    className="bg-secondary/50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Create Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={6}
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-full h-11 mt-6"
                  disabled={submitting}
                  data-testid="accept-invite-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    "Accept & Join"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
